import type { Site } from '@vista-digital/ocapi-types/v1'
import {
    configurationSettings,
    getAllSites,
    getSelectedSiteIds,
    getSelectedSites,
    loadAllSites,
    removeSelectedSite,
} from '@vista-digital/occ-domain'
import { Icon, Modal, useI18n } from '@vista-digital/occ-ui'
import { observer } from 'mobx-react'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useLogger } from '../../../hooks'
import { getFormattedSiteCount } from '../../../utils/strings/getFormattedSiteCount'
import type { ModalSitePickerDisplayTextPropsApi } from './models'
import { FilteredSitePickerComponent } from './subcomponents/FilteredSitePicker'
import type { SearchFormWrapperProps } from './subcomponents/SearchFormWrapper'
import SearchFormWrapper from './subcomponents/SearchFormWrapper'
import { getFilteredUnselectedSites } from './utils/getFilteredUnselectedSites'

export interface ModalSitePickerProps extends ModalSitePickerDisplayTextPropsApi {
    filterSiteIds?: string[]
    useIconButton?: boolean
    onSitesChanged?: () => void
    searchFormPosition?: 'in-header' | 'in-body'
    ctaClasses?: {
        containerClass?: string
        ctaClass?: string
    }
}

export const ModalSitePicker = observer(
    // eslint-disable-next-line complexity -- complexity is fine here
    forwardRef((props: ModalSitePickerProps, ref) => {
        const {
            sitePickerButtonMessage,
            sitePickerButtonLabel,
            saveCinemasLabel,
            filterSiteIds,
            useIconButton,
            searchFormPosition = 'in-header',
            ctaClasses: { containerClass = '', ctaClass = '' } = {},
            ...sitePickerDisplayText
        } = props

        const logger = useLogger('ModalSitePicker')

        const i18n = useI18n()

        const [isOpen, setIsOpen] = useState(false)

        // data for site picker header
        const allSites = getAllSites()
        const siteIds = getSelectedSiteIds()
        const [filteredSites, setFilteredSites] = useState<Site[]>()
        const [searchTerm, setSearchTerm] = useState('')

        useEffect(() => {
            void (async () => {
                try {
                    await loadAllSites()
                } catch (error) {
                    logger.error(`Failed to fetch sites. ${error}`)
                }
            })()
        }, [logger])

        const showModal = () => {
            setIsOpen(true)
        }

        const handleSitesModified = () => {
            if (props.onSitesChanged) {
                props.onSitesChanged()
            }
        }

        const handleSelectedSiteClicked = (siteId: string) => {
            removeSelectedSite(siteId)
            handleSitesModified()
        }

        useImperativeHandle(ref, () => ({
            showModal: showModal,
        }))

        const onOpenHandler = () => {
            setIsOpen(true)
        }

        const onCloseHandler = () => {
            setSearchTerm('')
            setFilteredSites(allSites)
            setIsOpen(false)
        }

        if (!allSites) {
            return null
        }

        const { maximumSelectedSites } = configurationSettings.data.browsing
        const selectedSites = getSelectedSites()
        const sitesToBeDisplayed = getFilteredUnselectedSites(filterSiteIds, filteredSites, i18n)

        const allSitesWithFilter = filterSiteIds?.length
            ? allSites.filter(s => filterSiteIds?.includes(s.id))
            : allSites

        const siteCountLabel =
            selectedSites.length < maximumSelectedSites
                ? getFormattedSiteCount(
                      sitePickerDisplayText.siteCountLabel,
                      sitesToBeDisplayed.length,
                      maximumSelectedSites,
                      i18n
                  )
                : getFormattedSiteCount(
                      sitePickerDisplayText.siteCountLabelMaximumSelected,
                      sitesToBeDisplayed.length,
                      maximumSelectedSites,
                      i18n
                  )
        const allSitesSelected = selectedSites.length >= Math.min(maximumSelectedSites, allSitesWithFilter.length)

        logger.trace('Rendering', { maximumSelectedSites, allSitesWithFilter, siteCountLabel, allSitesSelected })

        const searchFormWrapperProps: SearchFormWrapperProps = {
            allSites,
            sitePickerDisplayText,
            setFilteredSites,
            setSearchTerm,
            selectedSites,
            handleSelectedSiteClicked,
            sitesToBeDisplayed,
            allSitesSelected,
            siteCountLabel,
        }

        return (
            <>
                <Modal
                    show={isOpen}
                    onClose={onCloseHandler}
                    className="modal__site-picker"
                    heading={''}
                    closeButtonIconName="cross"
                    footer={
                        <div className={`v-modal-footer__content__cta-container ${containerClass}`}>
                            <div className={`cta ${ctaClass}`} role="button" onClick={onCloseHandler}>
                                {saveCinemasLabel}
                            </div>
                        </div>
                    }
                    header={
                        <>
                            {props.modalHeading && <h4 className="v-modal-header__title">{props.modalHeading}</h4>}
                            {searchFormPosition === 'in-header' && <SearchFormWrapper {...searchFormWrapperProps} />}
                        </>
                    }
                >
                    <>
                        {searchFormPosition === 'in-body' && <SearchFormWrapper {...searchFormWrapperProps} />}
                        <FilteredSitePickerComponent
                            displayText={sitePickerDisplayText}
                            siteIds={filterSiteIds}
                            searchTerm={searchTerm}
                            filteredSites={filteredSites}
                        />
                    </>
                </Modal>

                {!useIconButton && <span className="modal-site-picker-button-message">{sitePickerButtonMessage}</span>}
                {/* eslint-disable-next-line react/forbid-elements -- todo: refactor into a button - leaving for now as it breaks styling */}
                <a className="modal-site-picker-open-button" onClick={() => onOpenHandler()}>
                    {useIconButton && <Icon name={'location-pin'} />}

                    {!useIconButton && siteIds.length
                        ? `${sitePickerButtonLabel} (${siteIds.length})`
                        : `${sitePickerButtonLabel}`}
                    {useIconButton && siteIds.length > 0 && <div id="icon-selected-sites-number">{siteIds.length}</div>}
                </a>
            </>
        )
    })
)

export interface SiteModalHandles {
    showModal: () => boolean
}
